import { AssistantAdapter, HostAdapter } from "../types";
import { readCheckOutIdentity, writeCheckOutIdentity } from "./identity";
import { getWorkbookAsXlsx, describeSaveUnavailable, XLSX_MIME_TYPE } from "./bytes";
import { excelCloser } from "./close";
import { extractWorkbook } from "./extractWorkbook";
import { excelToolSchemas, excelSystemPrompt } from "./toolSchemas";
import { executeToolCall } from "./toolExecutor";
import { buildEditInfo } from "./editTracker";
import { enrichCitations, reveal, EXCEL_SEGMENT_ID_PATTERN } from "./citations";
import { WorkbookModel } from "./workbookModel";

// Excel: check out, Save, Check in, and an assistant that reads and edits the workbook.
//
// What made check out cheap was that nothing server-side had to change --
// DraftCheckOutMetadataStamper already stamps .xlsx with the same <jyloCheckOut> part
// it writes into .docx. The assistant is the opposite: almost nothing came across from
// Word, because a workbook's editable model (sheets, ranges, formulas) shares nothing
// with a document's (paragraphs, runs, revisions). What IS shared is the whole chat
// layer above the seam, which is the point of the seam.
//
// No `review`, deliberately. Excel has no revisions API, so there is no accept/reject
// and no synthetic undo stack was built to fake one. The absence of this property is
// what hides the buttons; see ChatMessageBubble.
//
// No `assembly` either: reference placeholders are Word content controls and Excel has
// no equivalent primitive.

const excelAssistant: AssistantAdapter = {
  extractDocumentModel: extractWorkbook,
  toolSchemas: excelToolSchemas,
  systemPrompt: excelSystemPrompt,
  executeToolCall,
  // Excel's cards need no document model to describe themselves: the tool arguments
  // already say which range changed and to what. Word needs it to look up the text a
  // deleted paragraph used to hold.
  describeEdit: (toolName, rawArgs, result) => buildEditInfo(toolName, rawArgs, result),
  suggestedPrompts: [
    "Explain what this workbook calculates",
    "Check the formulas on the active sheet for inconsistencies",
    "Summarise the totals on the active sheet",
    "Which cells are hard-coded where a formula would be expected?",
  ],
  segmentIdPattern: EXCEL_SEGMENT_ID_PATTERN,
  enrichCitations: (citations, docModel) => enrichCitations(citations, docModel as WorkbookModel),
  reveal,
};

export const excelAdapter: HostAdapter = {
  kind: "excel",
  displayName: "Excel",
  capabilities: {
    assembly: false,
  },

  manifestFileName: "manifest.excel.xml",

  documents: {
    fileExtension: "xlsx",
    mimeType: XLSX_MIME_TYPE,
    readCheckOutIdentity,
    writeCheckOutIdentity,
    getDocumentBytes: getWorkbookAsXlsx,
    describeSaveUnavailable,
    closer: excelCloser,
  },

  assistant: excelAssistant,
};
